'use client';

import * as React from 'react';
import styles from './Avatar.module.css';
import clsx from 'clsx';
import { isStandInAvatar } from './avatarSource';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  /**
   * Name to hash for the fallback tile's colour, when that should differ from
   * the name the initials come from. Anonymised candidates carry a rewritten
   * `name` ("Candidate 1"), which would recolour their tile and break the match
   * against the same person's avatar elsewhere on screen — pass the original
   * name here to hold the colour steady. Defaults to `name`.
   */
  colorName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  variant?: 'circle' | 'rounded' | 'square';
  status?: 'online' | 'offline' | 'busy' | 'away';
  /**
   * Obscures the candidate's likeness — anonymised candidates. Applied only to
   * a real picture: this component is the one that knows whether the image or
   * the initials tile ended up rendering, and blurring a stand-in hides nothing
   * while looking broken. @see isStandInAvatar
   */
  blur?: boolean;
  className?: string;
}

/**
 * Exported so surfaces that can't use this component — the public Float and
 * Submission pages render MUI Avatars carrying float-specific theming — can
 * still produce the identical initials fallback.
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** @see getInitials — same reason. */
export function getColorFromName(name: string): string {
  const colors = [
    '#13319a', // Primary Blue
    '#7C3AED', // Violet
    '#e50b71', // Secondary Pink
    '#F59E0B', // Amber
    '#10B981', // Emerald
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#8B5CF6', // Purple
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      colorName,
      size = 'md',
      variant = 'circle',
      status,
      blur = false,
      className,
      ...rest
    },
    ref
  ) => {
    // Tracks which src failed rather than a plain boolean, so a later src (an
    // upload replacing one that 404'd) is given a fresh chance to load instead
    // of being stuck on the fallback.
    const [erroredSrc, setErroredSrc] = React.useState<string | null>(null);
    const showFallback = !src || erroredSrc === src;
    const initials = name ? getInitials(name) : '?';
    const bgColor = name ? getColorFromName(colorName || name) : '#6B7280';

    return (
      <div
        ref={ref}
        className={clsx(styles.avatar, styles[size], styles[variant], className)}
        {...rest}
      >
        {showFallback ? (
          <div
            className={styles.fallback}
            style={{ backgroundColor: bgColor }}
            aria-label={name || 'Avatar'}
          >
            {initials}
          </div>
        ) : (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className={clsx(styles.image, blur && !isStandInAvatar(src) && styles.blurred)}
            onError={() => setErroredSrc(src)}
          />
        )}
        {status && <span className={clsx(styles.status, styles[status])} />}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Group
export interface AvatarGroupProps {
  children: React.ReactNode;
  max?: number;
  size?: AvatarProps['size'];
  className?: string;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  max = 4,
  size = 'md',
  className,
}) => {
  const childArray = React.Children.toArray(children);
  const visibleChildren = childArray.slice(0, max);
  const remaining = childArray.length - max;

  return (
    <div className={clsx(styles.group, className)}>
      {visibleChildren.map((child, index) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<AvatarProps>, {
              size,
              key: index,
            })
          : child
      )}
      {remaining > 0 && (
        <div className={clsx(styles.avatar, styles[size], styles.circle)}>
          <div className={styles.remaining}>+{remaining}</div>
        </div>
      )}
    </div>
  );
};
